import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Money } from "@/components/wallet/shared";
import { Minus, Plus, Search, ShoppingCart, Check } from "lucide-react";

const time = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

const BUYERS = [["student", "Studente", "addebito sul wallet"], ["teacher", "Docente", "in contanti"], ["cliente", "Cliente", "in contanti"]];

function Sold({ sales }) {
  const cash = sales.filter((s) => s.method === "contanti").reduce((a, s) => a + s.amount, 0);
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none" data-testid="bar-today-sales">
      <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">Le tue vendite di oggi ({sales.length})</div>
      {cash > 0 && <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900" data-testid="bar-cash-total">Contanti incassati oggi: <b>€ {cash.toFixed(2)}</b> — da consegnare all'economato</div>}
      {!sales.length && <div className="text-sm text-stone-400">Nessuna vendita.</div>}
      <div className="divide-y divide-stone-100">
        {sales.map((s) => (
          <div key={s.id} className="py-1.5 flex items-center gap-2 text-sm">
            <span className="font-mono text-xs text-stone-400 w-12">{time(s.created_at)}</span>
            <span className="flex-1 truncate">{s.student} · {s.label}</span>
            {s.method === "contanti" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">contanti</span>}
            <Money value={s.amount} className="text-xs"/>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function PortaleBar() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [buyerType, setBuyerType] = useState("student");
  const [customer, setCustomer] = useState("");
  const [student, setStudent] = useState(null);
  const [item, setItem] = useState(null);
  const [qty, setQty] = useState(1);
  const { data: ctx, error: ctxErr } = useQuery({ queryKey: ["bar-context"], retry: false, queryFn: async () => (await api.get("/bar/context")).data });
  const { data: sales = [] } = useQuery({ queryKey: ["bar-sales"], retry: false, queryFn: async () => (await api.get("/bar/sales/mine")).data });
  const list = useMemo(() => (ctx?.[buyerType === "teacher" ? "teachers" : "students"] || []).filter((s) => s.name.toLowerCase().includes(q.trim().toLowerCase())), [ctx, q, buyerType]);
  const isCash = buyerType !== "student";
  const ready = item && (buyerType === "cliente" || student);
  const buyerName = buyerType === "cliente" ? (customer.trim() || "Cliente") : student?.name;
  const pickType = (t) => { setBuyerType(t); setStudent(null); setQ(""); };
  const sell = useMutation({
    mutationFn: async () => (await api.post("/bar/sales", { buyer_type: buyerType, student_id: buyerType === "student" ? student.id : null,
      teacher_id: buyerType === "teacher" ? student.id : null, customer_name: buyerType === "cliente" ? customer : null, item_id: item.id, quantity: qty })).data,
    onSuccess: (d) => { toast.success(d.method === "contanti" ? `Incassati € ${d.amount.toFixed(2)} in contanti da ${d.student}` : `Addebitato a ${d.student}: ${d.label}`); setItem(null); setQty(1); setStudent(null); setQ(""); setCustomer(""); qc.invalidateQueries({ queryKey: ["bar-sales"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (ctxErr) {
    return <div className="p-10 text-center text-sm text-stone-500" data-testid="bar-not-enabled">Non sei abilitato alla cassa del bar.</div>;
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[900px] mx-auto space-y-5" data-testid="bar-pos-page">
      <PageHeader title="Cassa bar" subtitle="Studenti: addebito sul wallet. Docenti e clienti: incasso in contanti, senza debito."/>
      <Card className="p-4 border border-stone-200 rounded-xl shadow-none space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-stone-500">1 · Chi acquista</div>
        <div className="grid grid-cols-3 gap-2">
          {BUYERS.map(([v, l, hint]) => (
            <button key={v} onClick={() => pickType(v)} data-testid={`bar-buyer-${v}`}
              className={`rounded-xl border px-2 py-2 text-left transition-colors ${buyerType === v ? "border-[#1C352D] bg-[#1C352D] text-white" : "border-stone-200 hover:border-stone-400"}`}>
              <div className="text-sm font-medium">{l}</div><div className="text-[11px] opacity-75">{hint}</div>
            </button>
          ))}
        </div>
        {buyerType === "cliente" ? (
          <Input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Nome del cliente (facoltativo)" data-testid="bar-customer-name"/>
        ) : student ? (
          <div className="flex items-center justify-between rounded-lg bg-[#1C352D]/5 border border-[#1C352D]/20 px-3 py-2" data-testid="bar-selected-student">
            <b>{student.name}</b><button className="text-xs underline" onClick={() => setStudent(null)} data-testid="bar-change-student">Cambia</button>
          </div>
        ) : <>
          <div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={buyerType === "teacher" ? "Cerca docente…" : "Cerca nome o cognome…"} className="pl-8" data-testid="bar-student-search"/></div>
          <div className="max-h-56 overflow-y-auto divide-y divide-stone-100 border border-stone-100 rounded-lg">
            {list.map((s) => <button key={s.id} onClick={() => setStudent(s)} className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50" data-testid={`bar-student-${s.id}`}>{s.name}</button>)}
          </div>
        </>}
      </Card>
      <Card className="p-4 border border-stone-200 rounded-xl shadow-none space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-stone-500">2 · Prodotto</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(ctx?.items || []).map((it) => (
            <button key={it.id} onClick={() => setItem(it)} data-testid={`bar-item-${it.id}`}
              className={`rounded-xl border px-3 py-3 text-left transition-colors ${item?.id === it.id ? "border-[#1C352D] bg-[#1C352D] text-white" : "border-stone-200 hover:border-stone-400"}`}>
              <div className="text-sm font-medium">{it.name}</div><div className="text-xs opacity-75">€ {it.price.toFixed(2)}</div>
            </button>
          ))}
        </div>
        {!ctx?.items?.length && <div className="text-sm text-stone-400">Il listino è vuoto: chiedi all'economato di aggiungere i prodotti.</div>}
        <div className="flex items-center gap-3">
          <span className="text-sm text-stone-600">Quantità</span>
          <Button size="icon" variant="outline" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="bar-qty-minus"><Minus size={14}/></Button>
          <b className="w-6 text-center" data-testid="bar-qty">{qty}</b>
          <Button size="icon" variant="outline" onClick={() => setQty(Math.min(20, qty + 1))} data-testid="bar-qty-plus"><Plus size={14}/></Button>
        </div>
      </Card>
      <Button disabled={!ready || sell.isPending} onClick={() => sell.mutate()} data-testid="bar-charge-btn"
        className={`w-full h-12 rounded-full text-base ${isCash ? "bg-amber-600 hover:bg-amber-700" : "bg-[#E2725B] hover:bg-[#C95F49]"}`}>
        {sell.isPending ? <Check size={16} className="mr-2"/> : <ShoppingCart size={16} className="mr-2"/>}
        {ready ? (isCash ? `Incassa € ${(item.price * qty).toFixed(2)} in contanti · ${buyerName}` : `Addebita € ${(item.price * qty).toFixed(2)} a ${buyerName}`) : "Scegli chi acquista e il prodotto"}
      </Button>
      <Sold sales={sales}/>
    </div>
  );
}
